import { useMemo } from 'react';
import { useAuth } from '../../../App';
import { MODULES, MODULE_TYPES } from '../moduleRegistry';
import { useSupportingTools } from '../SupportingToolsContext';
import { useScreenPlacement } from './screenPlacement';

/**
 * Supporting Tools is granted screen by screen rather than as one module: every
 * screen writes straight into a client's production chit database, so "can open
 * Supporting Tools" is too coarse to be a useful permission.
 *
 * A screen's right key is the module key, a dot, then the screen's own key from
 * moduleRegistry.js — the same string SUPPORTING_TOOLS_SCREENS builds in
 * backend/app_modules.py. The two lists have to be kept in step when a screen is
 * added; a screen with no matching right is simply never shown, so the failure
 * mode is a missing screen rather than an unguarded one.
 *
 * Supporting Tools Live has its own module key and its own rights, built from
 * the same screen keys (supporting_tools_live.<screen>) — the backend derives
 * them from SUPPORTING_TOOLS_SCREENS, so nothing extra is kept in step.
 *
 * Four screens (Central Office Chits, Agent Code Delete, BPO Guarantor Id Delete
 * and Chit Pledges / Security Lien Remove) are reserved for super_admin. Nothing
 * special happens here for them: the server refuses to grant those keys to
 * anyone else, so they only ever appear for a user who actually holds them.
 */
export const SUPPORTING_TOOLS_MODULE = 'supporting_tools';
export const SUPPORTING_TOOLS_LIVE_MODULE = 'supporting_tools_live';

// Settings and the Make It Live button are open to admins and to anyone who holds
// Supporting Tools (the module or any one of its screens).
export function useCanManageSettings() {
  const { user } = useAuth();
  if (user?.role === 'super_admin' || user?.role === 'admin') return true;
  return (user?.allowed_modules || []).some(
    (m) => m === SUPPORTING_TOOLS_MODULE || m.startsWith(`${SUPPORTING_TOOLS_MODULE}.`),
  );
}

export const screenRightKey = (screenKey, module = SUPPORTING_TOOLS_MODULE) => `${module}.${screenKey}`;

/**
 * The screens the signed-in user may open here, the types that still have at
 * least one of them, and a predicate for guarding a screen that was reached
 * directly (a stale selection, or a pasted /screens/:key URL).
 *
 * "Here" is whichever of Supporting Tools / Supporting Tools Live this is
 * mounted in: a screen is offered only where it currently lives, and only if
 * the user holds that module's right for it.
 */
export function useAllowedScreens() {
  const { user } = useAuth();
  const { variant } = useSupportingTools();
  const { live, loading } = useScreenPlacement();
  const isLive = variant === 'live';
  const module = isLive ? SUPPORTING_TOOLS_LIVE_MODULE : SUPPORTING_TOOLS_MODULE;
  // allowed_modules is rebuilt server-side on every authenticated request, so
  // the array identity changes even when its contents do not; key the memo on
  // the contents to avoid recomputing on every render.
  const rightsKey = (user?.allowed_modules || []).join('|');
  const liveKey = [...live].sort().join('|');

  return useMemo(() => {
    const granted = new Set(rightsKey ? rightsKey.split('|') : []);
    const screens = loading
      ? []
      : MODULES.filter((m) => live.has(m.key) === isLive && granted.has(screenRightKey(m.key, module)));
    const allowedKeys = new Set(screens.map((m) => m.key));
    const types = MODULE_TYPES
      .map((t) => ({ ...t, modules: t.modules.filter((m) => allowedKeys.has(m.key)) }))
      .filter((t) => t.modules.length > 0);

    return {
      screens,
      types,
      loading,
      canOpen: (screenKey) => allowedKeys.has(screenKey),
      hasAny: screens.length > 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightsKey, liveKey, loading, isLive, module]);
}
